import {
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {IconChevronDown, IconChevronRight, IconPlus} from "@tabler/icons-react";
import {v4 as uuidv4} from "uuid";
import {ParserModal} from "../../../../../components/modals/ParserModal";
import {Editor} from "@monaco-editor/react";
import {toast} from "react-toastify";
import {useDebounce} from "../../../../../hooks/useDebounce";

const DataType = {
  STRING: "string",
  OBJECT: "object",
}


export const StartNodeSettings = ({node, setNode, onUpdate, project, onChangeData}) => {
  const debounced = useDebounce(node, 500)

  useEffect(() => {
    if (debounced?.data?.global_params) {
      onUpdate(debounced)
      console.log(debounced)
    }
  }, [debounced?.data?.global_params]);

  const onUpdateGlobalParam = (param) => {
    setNode(prev => ({
      ...prev,
      data: {
        ...prev.data,
        global_params: prev?.data?.global_params?.map(i => {
          if (i?.id === param?.id) {
            return {...param}
          }
          return {...i};
        })
      }
    }))
  }

  const deleteParam = (uid) => {
    setNode(prev => ({
      ...prev,
      data: {
        ...prev.data,
        global_params: prev?.data?.global_params?.filter(i => i?.id !== uid)
      }
    }))
  }

  const checkGlobalParamName = (name) => {
    const filteredParams = node?.data?.global_params?.filter(i => i?.name === name);
    if(filteredParams?.length > 1)
      toast.error("A global parameter with this name already exists. Please choose a different name to avoid overriding the existing parameter.");
  }

  return (
    <>
      <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={'center'}>
        <Typography fontWeight={700} fontSize={13}>Global Params</Typography>
        <IconButton
          variant={"outlined"} color={"secondary"} size={"small"}
          onClick={() => {
            setNode(prev => {
              const state = {...prev};
              let global_params = []
              if (state?.data?.global_params && Array.isArray(state?.data?.global_params) && state?.data?.global_params?.length > 0) {
                global_params = [...state.data.global_params]
              }
              global_params.push({
                id: uuidv4(),
                name: "",
                entity_type: null,
                data_type: DataType.STRING,
                value: ""
              })
              state.data.global_params = global_params;
              return state;
            })
          }}
        >
          <IconPlus size={18}/>
        </IconButton>
      </Stack>
      {
        node?.data?.global_params?.map((global_param, index) => {
          return (
            <GlobalParamItem
              key={global_param?.id}
              index={index}
              global_param={global_param}
              project={project}
              onUpdateParam={onUpdateGlobalParam}
              deleteParam={deleteParam}
              checkGlobalParamName={checkGlobalParamName}
            />
          )
        })
      }

      <Divider/>
    </>
  )
}


const GlobalParamItem = ({index, global_param, project, checkGlobalParamName, onUpdateParam, deleteParam}) => {
  // Implement global param item
  // make it collapsable
  const [expandValue, setExpandValue] = useState(false);
  const [open, setOpen] = useState(false);

  const onChange = (e) => {
    const key = e?.target?.name;
    const value = e?.target?.value;

    onUpdate(key, value === "none" ? null : value);
  }

  const onUpdate = (key, value) => {
    const param = {...global_param, [key]: value};
    console.log("update global param", param)
    onUpdateParam(param);
  }

  return (
    <>
      <ParserModal
        handleClose={() => setExpandValue(false)}
        open={expandValue}
        onSuccess={(content) => onUpdate("value", content)}
        text={global_param?.value}
      />
      <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}>
        <Typography fontWeight={700} fontSize={15} sx={{flex: 1}}>{index + 1}. {global_param?.name}</Typography>
        <IconButton onClick={() => setOpen(!open)} size={"small"}>{open ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}</IconButton>
      </Stack>
      {open && <Fade in={open}>
        <Stack direction={"column"} gap={1}>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography fontWeight={400} fontSize={13} sx={{flex: 1}}>Name</Typography>
            <TextField
              name={"name"} size={"small"}
              value={global_param?.name} sx={{flex: 2}}
              onChange={onChange}
              onBlur={() => checkGlobalParamName(global_param?.name)}
            />
          </Stack>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography fontWeight={400} fontSize={13} sx={{flex: 1}}>Entity Type</Typography>
            <Select
              name={"entity_type"} size={"small"}
              value={global_param?.entity_type || "none"}
              onChange={onChange} sx={{flex: 2}}
            >
              <MenuItem value={"none"}><em>None</em></MenuItem>
              {
                project?.entities?.map(entity => <MenuItem key={entity?.id}
                                                           value={entity?.name}>{entity?.name}</MenuItem>)
              }
            </Select>
          </Stack>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography fontWeight={400} fontSize={13} sx={{flex: 1}}>Data Type</Typography>
            <Select
              name={"data_type"} size={"small"}
              value={global_param?.data_type || "none"}
              onChange={onChange} sx={{flex: 2}}
            >
              <MenuItem value={DataType.STRING}>String</MenuItem>
              <MenuItem value={DataType.OBJECT}>Object</MenuItem>
            </Select>
          </Stack>
          <Stack direction={"column"} gap={1}>
            <Typography fontWeight={400} fontSize={13}>Value</Typography>
            {
              global_param?.data_type === DataType.STRING ?
                <TextField
                  name={"value"} size={"small"}
                  value={global_param?.value} sx={{flex: 2}}
                  onChange={onChange} multiline rows={3}
                /> :
                <Box onClick={() => setExpandValue(true)}>
                  <Editor
                    height="200px"
                    defaultLanguage="javascript"
                    value={global_param?.value || ""}
                    // onChange={(value, e) => onChangeData(isHook ? "api_output_parser" : "parser", value)}
                  />
                </Box>
            }
          </Stack>
          <Button color={"error"} onClick={() => deleteParam(global_param?.id)}>Delete Param</Button>
        </Stack>
      </Fade>}

      <Divider/>
    </>
  )
}